/* K2 model category pilot: exact type aliases + full-collection Boost facets. */
(function () {
  'use strict';
  const TYPE = 'pf_pt_product_type';
  const cleanKey = key => key.replace(/\[\]$/, '');
  const typeValues = url => [...url.searchParams].filter(([key]) => cleanKey(key) === TYPE).map(([, value]) => value);
  const removeTypes = params => {
    params.delete(TYPE);
    params.delete(TYPE + '[]');
  };

  function categoryUrl(source, aliases) {
    const url = new URL(source);
    removeTypes(url.searchParams);
    ['page', 'cursor', 'page_info'].forEach(key => url.searchParams.delete(key));
    [...new Set(aliases)].forEach(alias => url.searchParams.append(TYPE, alias));
    url.hash = 'k2-model-results';
    return url;
  }

  function summarize(payload, groups) {
    const facet = payload.filter?.options?.find(option => option.filterOptionId === TYPE);
    if (!facet || !Array.isArray(facet.values) || !Number.isFinite(payload.total_product)) throw new Error('Missing full type facet');
    const values = new Map();
    facet.values.forEach(value => {
      if (typeof value.key === 'string' && Number.isFinite(value.doc_count) && value.doc_count >= 0) values.set(value.key, value.doc_count);
    });
    return {
      total: payload.total_product,
      groups: groups.map(group => ({
        ...group,
        count: [...new Set(group.aliases)].reduce((sum, alias) => sum + (values.get(alias) || 0), 0),
        availableAliases: [...new Set(group.aliases)].filter(alias => (values.get(alias) || 0) > 0)
      }))
    };
  }

  function selectedGroup(groups, values) {
    return values.length ? groups.find(group => values.every(value => group.aliases.includes(value))) : null;
  }

  function supportedContext(source, config) {
    if (config.country !== 'US' || config.currency !== 'USD' || !/^en(?:-|$)/i.test(config.locale || '') || config.isB2B) return false;
    const params = new URL(source).searchParams;
    const supported = new Set([TYPE, 'pf_v_brand', 'pf_p_price', 'pf_r_review_ratings', 'pf_st_stock']);
    for (const [rawKey, value] of params) {
      const key = cleanKey(rawKey);
      if (key === 'q' || key.startsWith('filter.')) return false;
      if (key.startsWith('pf_') && !supported.has(key)) return false;
      if (key === 'pf_st_stock' && !['in-stock', 'out-of-stock'].includes(value)) return false;
    }
    return true;
  }

  // Keep API serialization separate from links: Boost's storefront uses repeated
  // keys, while its public API expects array brackets for list facets.
  function facetUrl(source, config) {
    const current = new URL(source);
    const url = new URL('https://services.mybcapps.com/bc-sf-filter/filter');
    const params = url.searchParams;
    Object.entries({shop: config.shop, collection_scope: config.collectionId, build_filter_tree: 'true', limit: '1', page: '1', product_available: 'false', variant_available: 'false', locale: config.locale, country: config.country, currency: config.currency, currency_rate: config.currencyRate, return_all_currency_fields: 'false'}).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    for (const [rawKey, value] of current.searchParams) {
      const key = cleanKey(rawKey);
      if (!key.startsWith('pf_') || key === TYPE) continue;
      // Values are passed verbatim; no free text, customer identity or tracking
      // query parameters are sent to the facet endpoint.
      if (key.startsWith('pf_st_')) params.append(key + '[]', value === 'out-of-stock' ? 'false' : 'true');
      else params.append(key + '[]', value);
    }
    params.sort();
    return url;
  }

  const helpers = {categoryUrl, summarize, selectedGroup, facetUrl, typeValues, supportedContext};
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('k2-model-categories')) return;

  const cache = new Map();
  class ModelCategories extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        // These public Shopify storefront context fields also feed Boost.
        this.config.currencyRate = window.Shopify?.currency?.rate;
        this.config.currency = window.Shopify?.currency?.active || this.config.currency;
        this.config.locale = window.Shopify?.locale || this.config.locale;
        this.config.country = window.Shopify?.country || this.config.country;
      }
      catch (_) { return; }
      this.onSync = () => {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.sync(), 180);
      };
      this.onClick = event => this.handleClick(event);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onSync);
      window.addEventListener('pageshow', this.onSync);
      // Observe only the existing Boost block. It provides a settled DOM change
      // after filter/sort actions without intercepting Boost, fetch or history.
      this.boostBlock = document.querySelector('.boost-sd__filter-product-list');
      this.observedContainer = this.boostBlock?.closest('.shopify-section') || this.boostBlock || document.getElementById('main-content');
      if (this.observedContainer) {
        this.observer = new MutationObserver(this.onSync);
        this.observer.observe(this.observedContainer, {childList: true, subtree: true});
      }
      if (this.boostBlock) {
        this.boostBlock.id ||= 'k2-model-results';
        if (!document.getElementById('k2-model-results')) {
          const anchor = document.createElement('span');
          anchor.id = 'k2-model-results';
          anchor.style.cssText = 'display:block;scroll-margin-top:160px';
          this.boostBlock.before(anchor);
        }
      }
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      clearTimeout(this.syncTimer);
      window.removeEventListener('popstate', this.onSync);
      window.removeEventListener('pageshow', this.onSync);
      this.removeEventListener('click', this.onClick);
      this.initialized = false;
    }

    async sync() {
      const current = new URL(location.href);
      if (!current.pathname.endsWith('/collections/' + this.config.handle)) {
        this.controller?.abort(); this.controller = null; this.requestPending = false;
        this.hidden = true; this.lastHref = null; this.removeAttribute('aria-busy'); return;
      }
      // The isolated pilot is validated for the current US/USD English market.
      // Other markets and filter dialects keep the ordinary Boost experience.
      if (!supportedContext(current, this.config)) {
        this.controller?.abort(); this.controller = null; this.requestPending = false;
        this.hidden = true; this.lastHref = null; this.removeAttribute('aria-busy'); return;
      }
      if (this.lastHref === current.href && (this.requestPending || this.dataExpires > Date.now())) return;
      this.lastHref = current.href;
      const requestUrl = facetUrl(current, this.config).href;
      this.renderLinks(current);
      this.controller?.abort();
      this.controller = null;
      this.requestPending = false;
      this.removeAttribute('aria-busy');
      if (this.data && requestUrl === this.dataKey && this.dataExpires > Date.now()) { this.render(current); return; }
      const controller = new AbortController();
      this.controller = controller;
      this.requestPending = true;
      this.setAttribute('aria-busy', 'true');
      // Reserve the existing layout while refreshed counts load. Busy content stays hidden.
      this.hidden = false;
      const timer = setTimeout(() => controller.abort(), 8000);
      try {
        let payload;
        const saved = cache.get(requestUrl);
        if (saved && saved.expires > Date.now()) payload = saved.payload;
        else {
          const response = await fetch(requestUrl, {signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer'});
          if (!response.ok) throw new Error('Facet request failed');
          payload = await response.json();
          // Validate before caching an API error or incomplete response.
          summarize(payload, this.config.groups);
          cache.set(requestUrl, {payload, expires: Date.now() + 120000});
          if (cache.size > 12) cache.delete(cache.keys().next().value);
        }
        if (controller.signal.aborted || this.controller !== controller) return;
        const latest = new URL(location.href);
        if (!latest.pathname.endsWith('/collections/' + this.config.handle) || !supportedContext(latest, this.config) || facetUrl(latest, this.config).href !== requestUrl) { this.lastHref = null; this.onSync(); return; }
        this.data = summarize(payload, this.config.groups);
        this.dataKey = requestUrl;
        this.dataExpires = Date.now() + 120000;
        this.render(new URL(location.href));
      } catch (_) {
        if (this.controller === controller) { this.hidden = true; this.data = null; this.lastHref = null; }
      } finally {
        clearTimeout(timer);
        if (this.controller === controller) { this.requestPending = false; this.removeAttribute('aria-busy'); }
      }
    }

    renderLinks(current) {
      this.querySelectorAll('[data-category-all], [data-category-clear]').forEach(link => { link.href = categoryUrl(current, []).href; });
    }

    render(current) {
      const values = typeValues(current);
      const active = selectedGroup(this.data.groups, values);
      let visible = 0;
      this.renderLinks(current);
      this.data.groups.forEach(group => {
        const card = this.querySelector('[data-category-key="' + group.key + '"]');
        if (!card) return;
        const selected = active?.key === group.key;
        card.hidden = group.count === 0 && !selected;
        if (card.hidden) return;
        const link = card.matches('a') ? card : card.querySelector('a');
        const aliases = group.availableAliases.length ? group.availableAliases : values.filter(value => group.aliases.includes(value));
        link.href = categoryUrl(current, aliases).href;
        link.dataset.resultCount = String(group.count);
        if (selected) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
        card.toggleAttribute('data-selected', selected);
        card.dataset.mobileExtra = String(visible >= 6 && !selected);
        card.dataset.desktopExtra = String(visible >= 10 && !selected);
        card.querySelector('span[data-category-count]').textContent = group.count.toLocaleString() + ' parts';
        visible++;
      });
      const status = this.querySelector('[data-category-status]');
      status.textContent = active ? 'Category: ' + active.label : values.length ? 'Custom product types selected' : 'All categories';
      this.querySelector('[data-category-clear]').hidden = !values.length;
      const all = this.querySelector('[data-category-all]');
      all.textContent = 'All parts (' + this.data.total.toLocaleString() + ')';
      if (!values.length) all.setAttribute('aria-current', 'true');
      else all.removeAttribute('aria-current');
      const expand = this.querySelector('[data-category-expand]');
      expand.hidden = visible <= 6;
      this.dataset.desktopExpand = String(visible > 10);
      this.hidden = visible === 0 && !values.length;
    }

    handleClick(event) {
      const expand = event.target.closest('[data-category-expand]');
      if (expand) {
        const expanded = this.dataset.expanded !== 'true';
        this.dataset.expanded = String(expanded);
        expand.setAttribute('aria-expanded', String(expanded));
        expand.textContent = expanded ? 'Show fewer categories' : 'Show all categories';
        return;
      }
      const link = event.target.closest('a');
      if (!link || !this.contains(link)) return;
      const key = link.closest('[data-category-key]')?.dataset.categoryKey;
      const clear = link.matches('[data-category-all], [data-category-clear]');
      if (!key && !clear) return;
      // Normal links preserve opening in a new tab, sharing and browser Back.
      // Existing Shopify events remain responsible for product/cart/purchase.
      try {
        if (this.config.analyticsEnabled && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true) {
          const published = window.Shopify.analytics?.publish('k2_model_category_click', {
            experiment: 'model_category_pilot_v1', model_collection_id: String(this.config.collectionId),
            model_handle: this.config.handle, category_key: key || 'all',
            result_count: Number(link.dataset.resultCount || this.data?.total || 0)
          });
          Promise.resolve(published).catch(() => {});
        }
      } catch (_) { /* Analytics must never prevent navigation. */ }
    }
  }
  customElements.define('k2-model-categories', ModelCategories);
})();
